import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getI18n, useTranslation } from "react-i18next";
import Button from "../../Button/Button";
import getClasses from "../../../utils/getClasses";
import { Template } from "../../../pages/Survey/Template/Template";
import FormInput from "../FormInput/FormInput";
import CustomTextArea from "../FormInput/CustomTextArea/CustomTextArea";
import { useForm, useFormState, useWatch } from "react-hook-form";
import {
  Category,
  IQuestion,
  LANGUAGE_TRANSLATION,
  PopulatedTemplate,
  TemplatePayload,
  initialTemplate,
  translations,
} from "../../../models/SurveyModels";
import FormSelect, { IOption } from "../FormInput/FormSelect/FormSelect";
import Step3 from "./Step3";
import {
  filteredArrayFromCurrentLanguage,
  getLocalTranslation,
} from "../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { RoutesRouter } from "../../../models/Routes";
import { useEndpoints } from "../../../hooks/useEndpoints";
import Translations, { IPopulated } from "../Translations";
require("./FormStep.scss");

interface IFormTemplate {
  templateData?: PopulatedTemplate;
  onSumbitForm: (data: TemplatePayload) => void;
  status: number;
  id?: string;
  isLoading: boolean;
  onSubmitCreate: (data: TemplatePayload) => void;
}
const FormStep: React.FC<IFormTemplate> = ({
  templateData,
  onSumbitForm,
  status,
  isLoading,
  onSubmitCreate,
  id,
}: IFormTemplate) => {
  const {
    reset,
    register,
    handleSubmit,
    clearErrors,
    control,
    setValue,
    unregister,
    getValues,
  } = useForm<TemplatePayload>({
    mode: "all",
  });
  const controls = useWatch({
    control,
  });

  const formState = useFormState({
    control,
  });
  const [currentStep, setCurrentStep] = useState(1);

  const { t } = useTranslation();
  const [template, setTemplate] = useState<IPopulated>(
    templateData || initialTemplate
  );
  const [translationsForIndex, setTranslations] = useState<IOption[][]>([
    translations,
  ]);
  const [selectedTranslations, setSelectedTranslations] = useState<string[]>(
    []
  );

  const [arrayFilterSubCategory, setArrayFilterSubCategory] = useState<
    Category[]
  >([]);
  const { getCategories } = useEndpoints();

  const navigate = useNavigate();
  const handleAddLanguage = () => {
    const newTranslation = {
      name: "",
      description: "",
      language: "",
    };
    setTemplate((oldValue) => ({
      ...oldValue!,
      translations: [...(oldValue?.translations || []), newTranslation],
    }));
    setValue(
      `translations.${controls.translations?.length || 0}`,
      newTranslation
    );
    setTranslations((prevTranslations) => [
      ...prevTranslations,
      translations.filter((item) =>
        selectedTranslations.every((term) =>
          Object.values(item).some(
            (value) => !value.toLowerCase().includes(term.toLowerCase())
          )
        )
      ),
    ]);
  };
  const removeLanguage = (index: number) => {
    const newTranslations = [...template.translations];
    newTranslations.splice(index, 1);
    setTemplate((oldValue) => ({
      ...oldValue,
      translations: newTranslations,
    }));
    setSelectedTranslations((old) => [...old, ...old.splice(index, 1)]);
    setTranslations((old) => [...old, ...old.splice(index, 1)]);
    unregister(`translations.${index}`);
  };

  useEffect(() => {
    getCategories({
      language: getI18n().language.includes("it") ? "ITALIAN" : "ENGLISH",
    }).then((resp) => {
      setArrayFilterSubCategory(resp);
    });
  }, [getCategories, t]);

  const onSubmit = (data: TemplatePayload) => {
    setCurrentStep((prevStep) => prevStep + 1);
    id ? onSumbitForm(data) : currentStep === 3 && onSubmitCreate(data);
  };

  useEffect(() => {
    if (id)
      currentStep > 3 && status === 200
        ? navigate(RoutesRouter.template)
        : currentStep > 3 && setCurrentStep(3);
  }, [currentStep, id, navigate, status]);

  useEffect(() => {
    if (templateData) {
      setTemplate(templateData);
      templateData.translations &&
        templateData.translations?.forEach(
          ({ language, description, name }, index) => {
            setValue(`translations.${index}`, { language, description, name });
            setSelectedTranslations((old) => {
              if (old.includes(language)) return [...old];
              else return [...old!, language];
            });
          }
        );
      setTranslations((prevTranslations) => {
        const newTranslations = translations.filter((item) =>
          selectedTranslations.every((term) =>
            Object.values(item).some(
              (value) => !value.toLowerCase().includes(term.toLowerCase())
            )
          )
        );
        const isDuplicate = prevTranslations.some((prevItem) => {
          if (prevItem.length !== newTranslations.length) {
            return false;
          }
          return prevItem.every((prevObj, index) => {
            const newObj = newTranslations[index];
            return Object.entries(prevObj).every(
              ([key, value]) => newObj[key] === value
            );
          });
        });

        if (isDuplicate) {
          return prevTranslations;
        } else {
          return [...prevTranslations, newTranslations];
        }
      });

      templateData?.questions &&
        templateData.questions.map((item, index) =>
          setValue(`questions.${index}`, item)
        );
      templateData?.image && setValue("image", templateData.image);
    } else {
      const newTranslation = {
        name: "",
        description: "",
        language: "",
      };
      setValue(`translations.${0}`, newTranslation);
    }
  }, [reset, setValue, templateData]);

  const renderStep1 = () => {
    return (
      <div className="col-lg-8 form-content step-1">
        <h3>{t("createTemplate.title")}</h3>
        <span>{t("createTemplate.subtitle")}</span>
        <Translations
          translationsforIndex={translationsForIndex}
          handleAddLanguage={handleAddLanguage}
          removeLanguage={removeLanguage}
          setSelectedTranslations={setSelectedTranslations}
          formState={formState}
          populated={controls as IPopulated}
          register={register}
          unregister={unregister}
          clearErrors={clearErrors}
          setValue={setValue}
          isLoading={isLoading}
        />
      </div>
    );
  };

  const renderStep2 = useMemo(() => {
    return currentStep === 2 ? (
      <Template
        templateDataQuestions={getValues("questions")}
        onChange={(value) => setValue("questions", value)}
        templateDataName={getValues(`translations.${0}.name`)}
        arrayFilterSubCategory={arrayFilterSubCategory}
      />
    ) : null;
  }, [currentStep, getValues, arrayFilterSubCategory, setValue]);

  const renderStep3 = () => {
    return (
      <Step3
        templateName={getValues(`translations.${0}.name`)}
        templateImage={getValues("image")}
        onChange={(value) => setValue("image", value)}
      />
    );
  };

  return (
    <div
      className={` container-form-step ${getClasses({
        "custom-form-container": currentStep !== 2,
      })} ${getClasses({
        "custom-form-template__step3": currentStep === 3,
      })} container-custom`}
    >
      <form className="form-content">
        <div className="container__top navbar fixed mb-4">
          <div className="container-step">
            <div
              onClick={() => {
                handleSubmit(onSumbitForm);
                setCurrentStep(1);
              }}
              className="step-info pointer"
            >
              <div
                className={`number-step ${getClasses({
                  active: currentStep === 1,
                })} `}
              >
                1
              </div>
              <span>{t("home.survey.template.formStep.title1")}</span>
            </div>
            <div
              onClick={() => {
                if (currentStep !== 1) {
                  handleSubmit(onSumbitForm);
                  setCurrentStep(2);
                }
              }}
              className={`step-info ${getClasses({
                pointer: currentStep !== 1,
              })}`}
            >
              <div
                className={`number-step ${getClasses({
                  active: currentStep === 2,
                })} `}
              >
                2
              </div>
              <span className="label">
                {t("home.survey.template.formStep.title2")}
              </span>
            </div>
            <div
              onClick={() => {
                if (currentStep === 3 || currentStep === 4) {
                  handleSubmit(onSumbitForm);
                  setCurrentStep(3);
                }
              }}
              className={`step-info ${getClasses({
                pointer: currentStep === 3 || currentStep === 4,
              })}`}
            >
              <div
                className={`number-step ${getClasses({
                  active: currentStep === 3 || currentStep === 4,
                })} `}
              >
                3
              </div>
              <span>{t("home.survey.template.formStep.title3")}</span>
            </div>
          </div>
          <div className="my-3">
            <Button
              disabled={
                (currentStep === 2 &&
                  !Boolean(getValues("questions")?.length > 0)) ||
                (currentStep === 3 && !Boolean(getValues("image")))
              }
              onClick={handleSubmit(onSubmit)}
            >
              {t(`surveyTemplate.create.${currentStep !== 3 ? "go" : "save"}`)}
            </Button>
          </div>
        </div>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2}
        {(currentStep === 3 || currentStep === 4) && renderStep3()}
      </form>
    </div>
  );
};

export default FormStep;
